import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  FileText,
  Save,
} from 'lucide-react';
import ImagePicker from '@/components/ImagePicker';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

const categories = [
  { value: 'grossesse', label: 'Grossesse' },
  { value: 'portee', label: 'Portée' },
  { value: 'actualite', label: 'Actualité' },
  { value: 'conseil', label: 'Conseil' },
];

// List View
export function AdminBlogList() {
  const { getAuthHeader } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/blog`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    
    try {
      const response = await fetch(`${API}/admin/blog/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        toast.success('Article supprimé');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="hover:text-sand">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold">Articles du blog</h1>
          </div>
          <Link to="/admin/blog/new">
            <Button className="bg-autumn hover:bg-autumn-dark">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel article
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <p className="text-center text-forest/60">Chargement...</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-forest/30 mx-auto mb-4" />
            <p className="font-body text-forest/60">Aucun article pour l'instant</p>
            <Link to="/admin/blog/new" className="text-autumn hover:underline mt-2 inline-block">
              Créer le premier article
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-xl p-4 shadow-soft flex items-center gap-4">
                {post.image_url && (
                  <img src={post.image_url} alt="" className="w-20 h-20 rounded-lg object-cover" />
                )}
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-forest">{post.title}</h3>
                  <p className="font-body text-sm text-forest/60">
                    {categories.find(c => c.value === post.category)?.label} • {new Date(post.published_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Link to={`/admin/blog/edit/${post.id}`}>
                    <Button variant="outline" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(post.id)} className="text-red-500 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// Form View (Create/Edit)
export function AdminBlogForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'actualite',
    image_url: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API}/blog/${id}`);
      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.title,
          content: data.content,
          category: data.category,
          image_url: data.image_url || ''
        });
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = isEdit ? `${API}/admin/blog/${id}` : `${API}/admin/blog`;
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isEdit ? 'Article mis à jour' : 'Article créé');
        navigate('/admin/blog');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/admin/blog" className="hover:text-sand">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-bold">
            {isEdit ? 'Modifier l\'article' : 'Nouvel article'}
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-soft space-y-6">
          <div>
            <Label className="font-body font-semibold text-forest">Titre *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="mt-1"
              placeholder="Titre de l'article"
              required
            />
          </div>

          <div>
            <Label className="font-body font-semibold text-forest">Catégorie *</Label>
            <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <ImagePicker
              label="Photo de l'article"
              value={formData.image_url}
              onChange={(url) => setFormData({...formData, image_url: url})}
            />
          </div>

          <div>
            <Label className="font-body font-semibold text-forest">Contenu *</Label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              className="mt-1 min-h-[300px]"
              placeholder="Contenu de l'article..."
              required
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={loading} className="bg-forest hover:bg-forest-dark">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Link to="/admin/blog">
              <Button type="button" variant="outline">Annuler</Button>
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
